import { useAuth0 } from "@auth0/auth0-react";
import { setUser as SentrySetUser } from "@sentry/react";
import React, { useCallback, useEffect, useRef } from "react";

import { LocalStorageKeys } from "globalConstants/localStorageKeys";
import Auth0Configs from "settings/auth0";
import i18n from "translations/i18n";
import { setItem } from "utils/localStorage";

import { UserSessionProvider } from ".";

const AUTH0_USER_METADATA = import.meta.env.VITE_AUTH0_USER_METADATA ?? "https://flowls.app/user_metadata";

const LOGIN_REDIRECT_GUARD_KEY = "flowls_auth0_login_redirect_guard";
const LOGIN_REDIRECT_GUARD_WINDOW_MS = 10_000;

// Silent renewal cannot satisfy these; only an interactive login can (MFA needs a prompt).
const RENEWAL_ERRORS_REQUIRING_INTERACTIVE_LOGIN = ["login_required", "consent_required", "mfa_required"];

const Auth0UserSessionProvider = ({ children }: { children: React.ReactNode }): JSX.Element => {
  const cachedIdTokenRef = useRef<string>();

  const { auth0Config, scope } = Auth0Configs();

  const { user, logout, isAuthenticated, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const getUserDataCallback = useCallback(() => {
    const userMetadata = user?.[AUTH0_USER_METADATA];

    return {
      name: user?.name,
      picture: user?.picture,
      email: user?.email,
      userPreference: {
        cardSize: userMetadata?.user_preference?.card_size,
        timezone: userMetadata?.user_preference?.timezone,
        datetimeFormat: userMetadata?.user_preference?.datetime_format,
        language: userMetadata?.user_preference?.language,
      },
    };
  }, [user]);

  const logoutCallback = useCallback(() => {
    logout?.({ logoutParams: { returnTo: window.location.origin } });
  }, [logout]);

  const redirectInFlightRef = useRef(false);

  const redirectToLogin = useCallback(() => {
    if (redirectInFlightRef.current) {
      return;
    }

    const lastRedirectAt = Number(sessionStorage.getItem(LOGIN_REDIRECT_GUARD_KEY) ?? 0);

    if (Date.now() - lastRedirectAt < LOGIN_REDIRECT_GUARD_WINDOW_MS) {
      console.error("Auth0 login redirect loop detected, aborting");

      logoutCallback();

      return;
    }

    redirectInFlightRef.current = true;
    sessionStorage.setItem(LOGIN_REDIRECT_GUARD_KEY, String(Date.now()));
    void loginWithRedirect();
  }, [loginWithRedirect, logoutCallback]);

  const handleRenewalError = useCallback(
    (error: unknown) => {
      const errorCode = (error as { error?: string })?.error ?? "";

      if (RENEWAL_ERRORS_REQUIRING_INTERACTIVE_LOGIN.includes(errorCode)) {
        redirectToLogin();

        return;
      }

      console.error(error);
    },
    [redirectToLogin],
  );

  const getIdToken = useCallback(
    async (options?: { forceRefresh?: boolean }): Promise<string | undefined> => {
      try {
        const detailedResponse = await getAccessTokenSilently({
          authorizationParams: { audience: auth0Config.audience, scope },
          cacheMode: options?.forceRefresh ? "off" : "on",
          detailedResponse: true,
        });

        cachedIdTokenRef.current = `Bearer ${detailedResponse.id_token}`;

        return cachedIdTokenRef.current;
      } catch (error) {
        handleRenewalError(error);

        return undefined;
      }
    },
    [getAccessTokenSilently, auth0Config.audience, scope, handleRenewalError],
  );

  const getCachedIdToken = useCallback((): string | undefined => cachedIdTokenRef.current, []);

  const onGetAccessTokenSilently = useCallback(async () => {
    try {
      const detailedResponse = await getAccessTokenSilently({
        authorizationParams: { audience: auth0Config.audience, scope },
        cacheMode: "off",
        detailedResponse: true,
      });

      cachedIdTokenRef.current = `Bearer ${detailedResponse.id_token}`;
    } catch (error) {
      console.error(error);
    }
  }, [getAccessTokenSilently, auth0Config.audience, scope]);

  useEffect(() => {
    const userMetadata = user?.[AUTH0_USER_METADATA];
    const userLanguage = userMetadata?.user_preference?.language;

    i18n.changeLanguage(userLanguage);
  }, [user]);

  useEffect(() => {
    const userPreferences = user?.[AUTH0_USER_METADATA]?.user_preference;

    if (userPreferences?.datetime_format) {
      setItem(LocalStorageKeys.DATETIME_FORMAT, userPreferences?.datetime_format);
    }

    if (userPreferences?.timezone) {
      setItem(LocalStorageKeys.TIMEZONE, userPreferences?.timezone);
    }

    if (user && import.meta.env.PROD) {
      SentrySetUser({ id: user.sub, username: user.nickname, email: user.email });
    }
  }, [user]);

  return (
    <UserSessionProvider
      value={{
        getUserData: getUserDataCallback,
        logout: logoutCallback,
        getIdToken,
        getCachedIdToken,
        redirectToLogin,
        onGetAccessTokenSilently,
        isAuthenticated,
      }}
    >
      {children}
    </UserSessionProvider>
  );
};

export default Auth0UserSessionProvider;
